import { routes } from "@/routes/utils";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  ADDRESS,
  ADDRESS_1,
  ADDRESS_2,
  ALTERNATE_LOGIN_ID,
  CITY,
  COMMUNITY,
  COMMUNITY_NAME,
  COUNTRY,
  DOB,
  EMAIL_ID,
  END_DATE,
  FIRST_NAME,
  LAST_NAME,
  MOBILE_NO,
  ORGANIZATION,
  PO_BOX,
  ROLE,
  START_DATE,
  STATE,
  USERNAME,
  ZIPCODE,
} from "./consts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
  transformActions,
  transformPermissions,
} from "@/lib/utils";
import { affiliationSchema, schema } from "./validations";
import { useEffect, useMemo, useState } from "react";
import {
  useAddEmployeeMutation,
  useDeleteEmployeeMutation,
  useDeleteEmployeeRoleMutation,
  useGetCommunities,
  useGetEmpAuditLogs,
  useGetEmployeeById,
  useGetPermissionByUserId,
  useGetState,
  useUpdateEmployeeAffiliationMutation,
  useUpdateEmployeeMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";

const useAddEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [openTabs, setOpenTabs] = useState<string[]>(["invoice"]);
  const [editAction, setIsEditAction] = useState<string>("view");
  const [countryCode, setCountryCode] = useState<string>("usa");
  const isView = id && editAction === "view";
  const isEditBasicDtl = id && editAction === "editBasicDtl";
  const isEditCommunity = id && editAction === "editCommunity";
  const isAdd = !id;
  const form = useForm<any>({
    defaultValues: {
      [USERNAME]: "",
      [ALTERNATE_LOGIN_ID]: "",
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [DOB]: "",
      [ORGANIZATION]: "",
      [MOBILE_NO]: "",
      [EMAIL_ID]: "",
      [ADDRESS]: {
        [ADDRESS_1]: "",
        [ADDRESS_2]: "",
        [STATE]: "",
        [CITY]: "",
        [ZIPCODE]: "",
        [PO_BOX]: "",
        [COUNTRY]: "USA",
      },
      [COMMUNITY]: Array.from({ length: 1 }, () => ({
        [COMMUNITY_NAME]: "",
        [ROLE]: "",
        [START_DATE]: new Date(),
        [END_DATE]: "",
      })),
    },
    resolver: zodResolver(
      isEditCommunity ? affiliationSchema : schema
    ),
  });

  const [openDeleteConfirmation, setOpenDeleteConfirmation] =
    useState<boolean>(false);

  const [openDeleteRoleConfirmation, setOpenDeleteRoleConfirmation] =
    useState<boolean>(false);
  const [deletedRoleIndex, setDeletedRoleIndex] = useState<string>("");

  const [communityAccess, setCommunityAccess] = useState<any>(null);

  const [keys, setKeys] = useState<any>({
    state: Math.random() * 3,
    city: Math.random() * 3,
    community: Math.random() * 3,
    role: Math.random() * 3,
  });

  const requiredFields = getRequiredFields(schema);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}/${routes.employee}`);
  };

  const title = useMemo(
    () => (id ? `${form.watch(FIRST_NAME)}'s Details` : "Add CAM"),
    [form.watch(FIRST_NAME)]
  );

  const breadcrumbData = [
    {
      link: `/${routes.admin}/${routes.masterData}/${routes.employee}`,
      name: `CAM`,
    },
    {
      link: "",
      name: title,
    },
  ];

  const { data: stateList, refetch: refetchStateList } = useGetState({
    countryCode: countryCode,
    queryParams: { enabled: !!countryCode },
  });

  //get communities
  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  const { data: auditLog, isLoading: isAuditLoading } = useGetEmpAuditLogs({
    data: {
      filters: [
        {
          field: "userId",
          value: id,
          operator: "EQUAL",
        },
      ],
      pageNumber: 0,
      pageSize: 20,
    },
    queryParams: {
      enabled: !!id,
    },
  });

  const { data: employeeDetails, isLoading: isEmployeeDetailsLoading } =
    useGetEmployeeById({
      id: id as string,
      queryParams: {
        enabled: !!id,
      },
    });

  const { data: userPermission, isLoading: isUserPermissionLoading } =
    useGetPermissionByUserId({
      userId: id as string,
      queryParams: {
        enabled: !!id,
      },
    });

  useEffect(() => {
    if (userPermission?.data) {
      const _permissionData = userPermission?.data?.map((data: any) => {
        return {
          ...data,
          permissions: transformActions(
            transformPermissions(data?.permissions)
          ),
        };
      });
      setCommunityAccess(_permissionData);
    }
  }, [userPermission]);

  useEffect(() => {
    if (employeeDetails?.data) {
      form.reset({
        [USERNAME]: employeeDetails?.data?.[USERNAME]?.toString() || "",
        [ALTERNATE_LOGIN_ID]:
          employeeDetails?.data?.[ALTERNATE_LOGIN_ID]?.toString() || "",
        [FIRST_NAME]: employeeDetails?.data?.[FIRST_NAME]?.toString() || "",
        [LAST_NAME]: employeeDetails?.data?.[LAST_NAME]?.toString() || "",
        [MOBILE_NO]: employeeDetails?.data?.[MOBILE_NO]?.toString() || "",
        [DOB]: employeeDetails?.data?.[DOB]?.toString() || "",
        [ORGANIZATION]: employeeDetails?.data?.[ORGANIZATION]?.toString() || "",
        [EMAIL_ID]: employeeDetails?.data?.[EMAIL_ID]?.toString() || "",
        [ADDRESS]: {
          [ADDRESS_1]:
            employeeDetails?.data?.[ADDRESS]?.[ADDRESS_1]?.toString() || "",
          [ADDRESS_2]:
            employeeDetails?.data?.[ADDRESS]?.[ADDRESS_2]?.toString() || "",
          [STATE]: employeeDetails?.data?.[ADDRESS]?.[STATE]?.toString() || "",
          [CITY]: employeeDetails?.data?.[ADDRESS]?.[CITY]?.toString() || "",
          [ZIPCODE]:
            employeeDetails?.data?.[ADDRESS]?.[ZIPCODE]?.toString() || "",
          [PO_BOX]:
            employeeDetails?.data?.[ADDRESS]?.[PO_BOX]?.toString() || "",
          [COUNTRY]: "USA",
        },
        [COMMUNITY]: employeeDetails?.data?.employeeCommunityRole?.map(
          (value: any) => ({
            ...value,
            [COMMUNITY_NAME]: value?.[COMMUNITY_NAME]?.toString() || "",
            [ROLE]: value?.[ROLE]?.toString() || "",
            [START_DATE]: value?.[START_DATE]?.toString() || "",
            [END_DATE]: value?.[END_DATE]?.toString() || "",
          })
        ),
      });
      setKeys({
        state: Math.random() * 2,
        city: Math.random() * 2,
        community: Math.random() * 2,
        role: Math.random() * 2,
      });
    }
  }, [employeeDetails]);

  const { mutate: addEmployee, isPending: isAddEmployeePending } =
    useAddEmployeeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        onBackClick();
        toast.success("Employee Added Successfully");
      },
    });

  const { mutate: updateEmployee, isPending: isUpdateEmployeePending } =
    useUpdateEmployeeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["employee", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["audit-logs"],
        });
        setIsEditAction("view");
        toast.success("Employee Updated Successfully");
      },
    });

  const { mutate: updateAffiliation, isPending: isUpdateAffiliationPending } =
    useUpdateEmployeeAffiliationMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["employee", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["permission", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["audit-logs"],
        });
        setIsEditAction("view");
        toast.success("Employee Community Affiliation Updated Successfully");
      },
    });

  const { mutate: deleteEmployee, isPending: isDeleteEmployeePending } =
    useDeleteEmployeeMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        onBackClick();
        toast.success("Employee Deleted Successfully");
      },
    });

  const { mutate: deleteEmployeeRole, isPending: isDeleteEmployeeRolePending } =
    useDeleteEmployeeRoleMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["employee", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["permission", id],
        });
        queryClient.invalidateQueries({
          queryKey: ["audit-logs"],
        });
        setOpenDeleteRoleConfirmation(false);
        toast.success("Employee Role Deleted Successfully");
      },
    });

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (id) {
      if (isEditCommunity) {
        updateAffiliation({
          userId: id,
          data: { [COMMUNITY]: form.watch(COMMUNITY) },
        });
      } else {
        updateEmployee({
          data: { ..._data },
          id,
        });
      }
    } else {
      addEmployee({
        data: { ..._data },
      });
    }
  };

  const onAddMore = () => {
    form.setValue(COMMUNITY, [
      ...form.getValues(COMMUNITY),
      {
        [COMMUNITY_NAME]: "",
        [ROLE]: "",
        [START_DATE]: new Date(),
        [END_DATE]: "",
      },
    ]);
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs([e[e.length - 1]]);
  };

  const onDeleteRoleClick = (index: string) => {
    setDeletedRoleIndex(index);
    setOpenDeleteRoleConfirmation(true);
  };

  const onDeleteCommunityRole = () => {
    const current = form.watch(COMMUNITY);
    const deletedRole = current[deletedRoleIndex];

    if (deletedRole?.userRoleId) {
      deleteEmployeeRole({ roleId: deletedRole.userRoleId });
    } else {
      form.setValue(
        COMMUNITY,
        current.filter((_: any, idx: string) => idx !== deletedRoleIndex)
      );
      setOpenDeleteRoleConfirmation(false);
    }
  };

  const onDeleteEmployee = () => {
    setOpenDeleteConfirmation(true);
  };

  const onDeleteConfirm = () => {
    deleteEmployee({ id: id as string });
  };

  const onDeleteWhileAdd = (index: string) => {
    const current = form.watch(COMMUNITY);
    form.setValue(
      COMMUNITY,
      current.filter((_: any, idx: string) => idx !== index)
    );
  };

  const onEditIconClick = () => {
    setIsEditAction("editBasicDtl");
  };

  const onCommunityEdit = () => {
    setIsEditAction("editCommunity");
  };

  const onCancelCommunityEdit = () => {
    form.setValue(
      COMMUNITY,
      employeeDetails?.data?.employeeCommunityRole?.map((value: any) => ({
        ...value,
        [COMMUNITY_NAME]: value?.[COMMUNITY_NAME]?.toString() || "",
        [ROLE]: value?.[ROLE]?.toString() || "",
        [START_DATE]: value?.[START_DATE]?.toString() || "",
        [END_DATE]: value?.[END_DATE]?.toString() || "",
      }))
    );
    setIsEditAction("view");
  };

  const onCancelBasicDtlEdit = () => {
    form.reset({
      ...form.getValues(),
      [USERNAME]: employeeDetails?.data?.[USERNAME]?.toString() || "",
      [ALTERNATE_LOGIN_ID]:
        employeeDetails?.data?.[ALTERNATE_LOGIN_ID]?.toString() || "",
      [FIRST_NAME]: employeeDetails?.data?.[FIRST_NAME]?.toString() || "",
      [LAST_NAME]: employeeDetails?.data?.[LAST_NAME]?.toString() || "",
      [MOBILE_NO]: employeeDetails?.data?.[MOBILE_NO]?.toString() || "",
      [DOB]: employeeDetails?.data?.[DOB]?.toString() || "",
      [ORGANIZATION]: employeeDetails?.data?.[ORGANIZATION]?.toString() || "",
      [EMAIL_ID]: employeeDetails?.data?.[EMAIL_ID]?.toString() || "",
      [ADDRESS]: {
        [ADDRESS_1]:
          employeeDetails?.data?.[ADDRESS]?.[ADDRESS_1]?.toString() || "",
        [ADDRESS_2]:
          employeeDetails?.data?.[ADDRESS]?.[ADDRESS_2]?.toString() || "",
        [STATE]: employeeDetails?.data?.[ADDRESS]?.[STATE]?.toString() || "",
        [CITY]: employeeDetails?.data?.[ADDRESS]?.[CITY]?.toString() || "",
        [ZIPCODE]:
          employeeDetails?.data?.[ADDRESS]?.[ZIPCODE]?.toString() || "",
        [PO_BOX]: employeeDetails?.data?.[ADDRESS]?.[PO_BOX]?.toString() || "",
        [COUNTRY]: "USA",
      },
    });
    setIsEditAction("view");
  };

  const communityRoles = useWatch({ control: form.control, name: COMMUNITY });

  useEffect(() => {
    if (!communityRoles?.length) return;

    const groupedByCommunity: Record<string, number[]> = {};

    // 🔹 Group indices by communityId
    communityRoles.forEach((item: any, index: number) => {
      const { communityId } = item;
      if (!groupedByCommunity[communityId]) {
        groupedByCommunity[communityId] = [];
      }
      groupedByCommunity[communityId].push(index);
    });

    Object.values(groupedByCommunity).forEach((indices) => {
      if (indices.length < 2) return;

      // Oldest = lowest index, Newest = highest index
      const minIndex = Math.min(...indices);
      const maxIndex = Math.max(...indices);

      const communityA = form.getValues(
        `${COMMUNITY}.${minIndex}.${COMMUNITY}`
      );
      const communityB = form.getValues(
        `${COMMUNITY}.${maxIndex}.${COMMUNITY}`
      );
      const roleA = form.getValues(`${COMMUNITY}.${minIndex}.${ROLE}`);
      const roleB = form.getValues(`${COMMUNITY}.${maxIndex}.${ROLE}`);
      const endDateA = form.getValues(`${COMMUNITY}.${minIndex}.${END_DATE}`);
      const startDateB = form.getValues(
        `${COMMUNITY}.${maxIndex}.${START_DATE}`
      );

      // 1️⃣ Duplicate check (same role + no endDate)
      if (communityA === communityB && roleA === roleB && !endDateA) {
        toast.error("This community with the same role already exists");
        form.setValue(`${COMMUNITY}.${maxIndex}.${ROLE}`, "");
        form.setValue(`${COMMUNITY}.${maxIndex}.${COMMUNITY_NAME}`, "");
        form.setValue(`${COMMUNITY}.${maxIndex}.${START_DATE}`, new Date());
        return;
      }

      // 2️⃣ Same role: ensure new startDate > old endDate
      if (communityA === communityB && roleA === roleB && endDateA) {
        if (new Date(endDateA) >= new Date(startDateB)) {
          toast.error(
            "New role start date must be after previous role end date"
          );
          return;
        }
      }

      // 3️⃣ Different roles in same community → close old role
      if (communityA === communityB && roleB && roleA !== roleB) {
        const today = new Date().toISOString().split("T")[0];
        if (!endDateA) {
          form.setValue(`${COMMUNITY}.${minIndex}.${END_DATE}`, today);
        }
      }
    });
  }, [communityRoles]);

  const onPhoneCountryChange = (_: any, country: any) => {
    const code =
      country?.countryCode == "us"
        ? "usa"
        : country?.countryCode == "in"
        ? "IND"
        : country?.countryCode;
    if (code != countryCode) {
      setCountryCode(code);
      form.setValue(`${ADDRESS}.${STATE}`, "");
      setTimeout(() => refetchStateList(), 100);
    }
  };

  const formatDescription = (text?: string) => {
    if (!text) return "";

    return text
      .toLowerCase()
      .split("_")
      .map((word) => {
        const capitalized = word.charAt(0).toUpperCase() + word.slice(1);

        // Naive past tense handling
        if (capitalized.endsWith("e")) return capitalized + "d";
        if (capitalized.endsWith("y")) return capitalized.slice(0, -1) + "ied";
        return capitalized + "ed";
      })
      .join(" ");
  };

  return {
    isView,
    isAdd,
    title,
    onBackClick,
    breadcrumbData,
    form,
    requiredFields,
    onCancel: onBackClick,
    stateList,
    onSubmit,
    keys,
    communityList,
    isCommunityLoading,
    onAddMore,
    openTabs,
    setOpenTabs,
    onAccordionChange,
    addEmployee,
    isAddEmployeePending,
    onDeleteCommunityRole,
    isEmployeeDetailsLoading,
    isUpdateEmployeePending,
    communityAccess,
    onDeleteEmployee,
    openDeleteConfirmation,
    setOpenDeleteConfirmation,
    onDeleteConfirm,
    isDeleteEmployeePending,
    onEditIconClick,
    isEditBasicDtl,
    isEditCommunity,
    id,
    onCommunityEdit,
    onCancelCommunityEdit,
    onCancelBasicDtlEdit,
    isUpdateAffiliationPending,
    onDeleteRoleClick,
    deletedRoleIndex,
    openDeleteRoleConfirmation,
    setOpenDeleteRoleConfirmation,
    isDeleteEmployeeRolePending,
    onDeleteWhileAdd,
    isUserPermissionLoading,
    auditLog,
    isAuditLoading,
    onPhoneCountryChange,
    formatDescription,
    countryCode,
  };
};

export default useAddEdit;
